import React from "react";
import CodePill from "../../../../shared/CodePill/CodePill";
import WorkCodeField from "../../../../shared/WorkCodeField/WorkCodeField";
import { swatchHex } from "../../../../../utils/color";
import type { ActivityCommitted, ActivityPatch, CodeEdit } from "../../state";
import { levelKey, zoneKey } from "../../state";
import { buildCommitted } from "../../tree";
import type { SectionMeta, TreeActivity } from "../../tree";
import type { ExistingWorkCode } from "../../../../../models";
import "./ActivityRow.css";

const STATUS_PILLS: Record<string, { label: string; cls: string }> = {
  TK_NotStart: { label: "Not Started", cls: "takt-editor-status--notstart" },
  TK_Active: { label: "In Progress", cls: "takt-editor-status--active" },
  TK_Complete: { label: "Completed", cls: "takt-editor-status--complete" },
};

/** Short "Mon D" from a P6 date string ("2026-01-30 08:00" / "2026-01-30"). */
const fmtDate = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso.replace(" ", "T"));
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/** Stand-in while a row isn't editing — keeps the heavy getSectionMeta board
 *  scan out of the render path of every non-editing row. */
const EMPTY_META: SectionMeta = { taktAs: [], zones: [], levelsByZone: {} };

interface Props {
  scheduleId: number;
  activity: TreeActivity;
  /** Per-activity overlay staged in the editor (over the committed values). */
  patch?: ActivityPatch;
  /** {code: hex} for the committed chip fill. */
  colors: Record<string, string>;
  /** Board-wide work-code edits (Work Codes tab), keyed `code|prefix` — cascade
   *  a rename/recolor/recode onto every activity that carries the code. */
  codeEdits: Record<string, CodeEdit>;
  /** Just dropped here — glow until the next click. */
  highlighted?: boolean;
  /** This is the anchor of a just-dropped set — scroll it into view. */
  scrollHint?: boolean;
  /** Selected for bulk drag. */
  selected?: boolean;
  /** Total selected — drives the bulk drag-preview count. */
  selectionCount: number;
  onToggleSelect: (activityId: number) => void;
  sectionOptions: string[];
  /** A/B/C actually present in a section — scopes the placement dropdowns. */
  getSectionMeta: (section: string) => SectionMeta;
  /** Picking a different section lazy-loads it so its A/B/C populate. */
  onNeedSection: (section: string) => void;
  onApply: (activityId: number, patch: ActivityPatch, committed: ActivityCommitted) => void;
  onDragStart: (activityId: number, committed: ActivityCommitted) => void;
  onDragEnd: () => void;
  /** Remove this one activity from the board (full untag → untagged rail). Shown
   *  only on placed rows; the zone/level structure is kept by the host. */
  onRemove?: (activityId: number) => void;
  /** Work codes created/edited earlier this session — so a code you just made is
   *  reusable when inline-editing another activity's code. */
  sessionCodes?: ExistingWorkCode[];
  /** Staged section/zone/level renames — the placement dropdowns show the new
   *  label, while the option VALUE stays the raw Takt key (grouping/commit key
   *  off the raw value until publish). */
  sectionNames?: Record<string, string>;
  zoneNames?: Record<string, string>;
  levelNames?: Record<string, string>;
}

const ActivityRow: React.FC<Props> = ({
  scheduleId,
  activity,
  patch,
  colors,
  codeEdits,
  highlighted,
  scrollHint,
  selected,
  selectionCount,
  onToggleSelect,
  sectionOptions,
  getSectionMeta,
  onNeedSection,
  onApply,
  onDragStart,
  onDragEnd,
  onRemove,
  sessionCodes,
  sectionNames,
  zoneNames,
  levelNames,
}) => {
  // Committed values resolve a board-wide code edit (Work Codes tab) first, so a
  // rename/recolor/recode there shows on every activity carrying the code.
  const committed = buildCommitted(activity, codeEdits, colors);
  const bulk = Boolean(selected) && selectionCount > 1;
  // Per-activity overlay wins over the board edit for this activity.
  const eff = {
    code: patch?.code ?? committed.code,
    name: patch?.name ?? committed.name,
    color: patch?.color ?? committed.color,
    section: patch?.section ?? committed.section,
    takt_a: patch?.takt_a ?? committed.takt_a,
    takt_b: patch?.takt_b ?? committed.takt_b,
    takt_c: patch?.takt_c ?? committed.takt_c,
  };
  const swatch = eff.code ? swatchHex(eff.color, eff.code) : null;
  const pill = activity.status ? STATUS_PILLS[activity.status] : undefined;
  const leaf = activity.wbs_path[activity.wbs_path.length - 1] ?? "";
  const dates =
    activity.start || activity.finish
      ? `${fmtDate(activity.start)} – ${fmtDate(activity.finish)}`
      : "";

  const [editing, setEditing] = React.useState(false);
  const [buf, setBuf] = React.useState(eff);
  const previewRef = React.useRef<HTMLDivElement>(null);
  const rowRef = React.useRef<HTMLDivElement>(null);
  const formRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (scrollHint) rowRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [scrollHint]);

  // Opened near the bottom of a scroll area, the form would tuck under the edge.
  React.useEffect(() => {
    if (editing) formRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [editing]);

  // Opening seeds the buffer, closing commits it (one undo step) via the state
  // layer's diff-merge. An untagged row defaults to the section it sits in.
  // Save the hue the swatch shows — the picked color, or the code's derived hue
  // when the user never opened the color picker (else the code saves with no
  // color and the board renders the bar uncolored). Mirrors BulkAssign /
  // UntaggedGroups, the editor's other code-assign surfaces.
  const commit = (b: typeof buf) =>
    onApply(
      activity.id,
      { ...b, color: b.code ? swatchHex(b.color, b.code) : b.color },
      committed,
    );
  const open = () => {
    const sec = eff.section || activity.loaded_section || "";
    const meta = getSectionMeta(sec);
    setBuf({ ...eff, section: sec, takt_a: eff.takt_a || meta.taktAs[0] || "" });
    if (sec) onNeedSection(sec);
    setEditing(true);
  };
  const close = (b: typeof buf) => {
    commit(b);
    setEditing(false);
  };
  const toggle = () => (editing ? close(buf) : open());
  const set = (p: Partial<typeof buf>) => setBuf((prev) => ({ ...prev, ...p }));
  // Changing the section rescopes phase/zone/level to that section's values.
  const setSection = (newSec: string) => {
    const meta = getSectionMeta(newSec);
    setBuf((b) => ({ ...b, section: newSec, takt_a: meta.taktAs[0] || "", takt_b: "", takt_c: "" }));
    onNeedSection(newSec);
  };
  const setZone = (z: string) => setBuf((b) => ({ ...b, takt_b: z, takt_c: "" }));

  React.useEffect(() => {
    if (!editing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close(buf);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const dropOptions = (opts: string[], current: string) =>
    opts.includes(current) ? opts : [current, ...opts];

  // Placement dropdowns are scoped to the chosen section's actual A/B/C. Only
  // the open edit form needs them, so skip the board scan on non-editing rows.
  const meta = editing ? getSectionMeta(buf.section) : EMPTY_META;
  const phaseOpts = dropOptions(meta.taktAs, buf.takt_a);
  const zoneOpts = dropOptions(meta.zones, buf.takt_b);
  const levelOpts = dropOptions(meta.levelsByZone[buf.takt_b] ?? [], buf.takt_c);

  return (
    <div className="takt-editor-activity-wrap">
      <div
        ref={rowRef}
        className={`takt-editor-activity ${highlighted ? "is-moved" : ""} ${
          editing ? "is-editing" : ""
        } ${selected ? "is-selected" : ""}`}
        draggable
        onClick={toggle}
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", String(activity.id));
          if (previewRef.current) e.dataTransfer.setDragImage(previewRef.current, 12, 12);
          onDragStart(activity.id, committed);
        }}
        onDragEnd={onDragEnd}
        title={activity.task_name}
      >
        <div className="takt-editor-activity-line">
          <input
            type="checkbox"
            className="takt-editor-activity-check"
            checked={Boolean(selected)}
            onClick={(e) => e.stopPropagation()}
            onChange={() => onToggleSelect(activity.id)}
            aria-label="Select for bulk move"
            title="Select for bulk move"
          />
          <span className="takt-editor-activity-grip" title="Drag to move" aria-hidden>
            ⠿
          </span>
          <CodePill code={eff.code || "—"} color={swatch} className="takt-editor-codepill" />
          {eff.name && <span className="takt-editor-codename">{eff.name}</span>}
          <span className="takt-editor-activity-name">{activity.task_name}</span>
          {onRemove && eff.takt_b && (
            <button
              type="button"
              className="takt-editor-activity-remove"
              title="Remove from board — drops this activity's code + placement, back to the untagged rail (the zone/level row stays)"
              aria-label="Remove from board"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(activity.id);
              }}
            >
              ✕
            </button>
          )}
        </div>
        <div className="takt-editor-activity-meta">
          {leaf && (
            <span className="takt-editor-meta-wbs" title={activity.wbs_path.join(" › ")}>
              {leaf}
            </span>
          )}
          {dates && <span className="takt-editor-meta-dates">{dates}</span>}
          {pill && (
            <span className={`takt-editor-meta-status ${pill.cls}`}>{pill.label}</span>
          )}
        </div>
      </div>

      {/* Off-screen drag image — the activity, or a count when dragging a set. */}
      <div ref={previewRef} className="takt-editor-drag-preview" aria-hidden>
        {bulk ? (
          <span className="takt-editor-drag-preview-name">{selectionCount} activities</span>
        ) : (
          <>
            <CodePill code={eff.code || "—"} color={swatch} className="takt-editor-codepill" />
            <span className="takt-editor-drag-preview-name">{activity.task_name}</span>
          </>
        )}
      </div>

      {editing && (
        <div ref={formRef} className="takt-editor-activity-form" onClick={(e) => e.stopPropagation()}>
          <div className="takt-editor-form-block takt-editor-form-block--code">
            <div className="takt-editor-field-overline">Work code</div>
            <WorkCodeField
              scheduleId={scheduleId}
              code={buf.code}
              name={buf.name}
              color={buf.color}
              onChange={(p) => set(p)}
              extraCodes={sessionCodes}
            />
          </div>

          <div className="takt-editor-form-block takt-editor-form-block--place">
            <div className="takt-editor-field-overline">Placement</div>
            <div className="takt-editor-field-row">
              <label className="takt-editor-field takt-editor-field--section">
                Section
                <select value={buf.section} onChange={(e) => setSection(e.target.value)}>
                  {dropOptions(sectionOptions, buf.section).map((s) => (
                    <option key={s} value={s}>
                      {(sectionNames?.[s] ?? s) || "—"}
                    </option>
                  ))}
                </select>
              </label>
              <label className="takt-editor-field">
                Phase
                <select value={buf.takt_a} onChange={(e) => set({ takt_a: e.target.value })}>
                  {phaseOpts.map((a) => (
                    <option key={a} value={a}>
                      {a || "—"}
                    </option>
                  ))}
                </select>
              </label>
              <label className="takt-editor-field">
                Zone
                <select value={buf.takt_b} onChange={(e) => setZone(e.target.value)}>
                  {zoneOpts.map((z) => (
                    <option key={z} value={z}>
                      {(zoneNames?.[zoneKey(buf.section, z)] ?? z) || "—"}
                    </option>
                  ))}
                </select>
              </label>
              <label className="takt-editor-field">
                Level
                <select value={buf.takt_c} onChange={(e) => set({ takt_c: e.target.value })}>
                  {levelOpts.map((c) => (
                    <option key={c} value={c}>
                      {(levelNames?.[levelKey(buf.section, buf.takt_b, c)] ?? c) || "—"}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          <button type="button" className="takt-editor-form-close" onClick={() => close(buf)}>
            Done
          </button>
        </div>
      )}
    </div>
  );
};

export default ActivityRow;
